import * as React from "react";
import moment from "moment";
import { Chip } from "@mui/material";
import { describeExpiration, ExpirationStatus, getExpiration } from "../../../Utils/expiration";
import "./DetailView.css";

// Formato común del modal de información de todas las secciones:
// cabecera (título, descripción y chips), rejilla de tarjetas de 3 columnas
// en la que todas miden lo mismo, y comentario opcional a todo el ancho.

export type ChipColor = "default" | "success" | "warning" | "error" | "info";

export const isEmpty = (value: any) =>
  value === null || value === undefined || String(value).trim() === "";

export const text = (value: any) => (isEmpty(value) ? null : value);

export const formatDate = (value: any) =>
  isEmpty(value) ? null : moment(value).format("DD/MM/YYYY");

export const yesNo = (value: any) =>
  value === null || value === undefined ? null : value ? "Sí" : "No";

// Solo se enlazan URLs http(s) para no ejecutar "javascript:" u otros esquemas
const safeUrl = (value: string) => {
  const url = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.href : null;
  } catch {
    return null;
  }
};

// Devuelven null si no hay valor, para que el campo muestre "Sin datos"
export const linkValue = (url: any) => {
  if (isEmpty(url)) return null;
  const href = safeUrl(String(url));
  if (!href) return url;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" title={url}>
      {url}
    </a>
  );
};

export const emailValue = (email: any) => {
  if (isEmpty(email)) return null;
  return (
    <a href={`mailto:${email}`} title={email}>
      {email}
    </a>
  );
};

export function DetailField(props: { label: string; children?: React.ReactNode }) {
  const { label, children } = props;
  const empty = children === null || children === undefined || children === "";
  return (
    <div className="detailField">
      <dt>{label}</dt>
      <dd
        className={empty ? "detailEmpty" : ""}
        title={typeof children === "string" || typeof children === "number" ? String(children) : undefined}
      >
        {empty ? "Sin datos" : children}
      </dd>
    </div>
  );
}

export function DetailCard(props: { title: string; children: React.ReactNode }) {
  return (
    <section className="detailSection">
      <h3 className="detailSectionTitle">{props.title}</h3>
      <dl className="detailGrid">{props.children}</dl>
    </section>
  );
}

const expirationChipColor: Record<ExpirationStatus, ChipColor> = {
  expired: "error",
  warning: "warning",
  valid: "success",
};

// Mismo criterio de color que ExpirationDate: rojo vencida, naranja dentro de
// los días de aviso (advise_days), verde vigente
export function ExpirationChip(props: { date: any; adviseDays?: any }) {
  const expiration = getExpiration(props.date, props.adviseDays);
  if (!expiration) {
    return <Chip size="small" label="Sin vencimiento" />;
  }
  const { status, formatted } = expiration;
  const label =
    status === "valid"
      ? `Vence el ${formatted}`
      : `${describeExpiration(expiration)} (${formatted})`;
  return <Chip size="small" color={expirationChipColor[status]} label={label} />;
}

type DetailViewProps = {
  title: React.ReactNode;
  description?: React.ReactNode;
  badges?: React.ReactNode;
  comment?: any;
  children: React.ReactNode; // tarjetas (DetailCard)
};

export default function DetailView({ title, description, badges, comment, children }: DetailViewProps) {
  return (
    <div className="detailView">
      <header className="detailHeader">
        <div>
          <h2 className="detailTitle">{title}</h2>
          {isEmpty(description) ? null : <p className="detailDescription">{description}</p>}
        </div>
        {badges ? <div className="detailBadges">{badges}</div> : null}
      </header>

      <div className="detailCards">{children}</div>

      {isEmpty(comment) ? null : (
        <section className="detailSection">
          <h3 className="detailSectionTitle">Comentario</h3>
          <p className="detailComment">{comment}</p>
        </section>
      )}
    </div>
  );
}
